import { createPortal } from "react-dom";
import { type VariantProps, cva, cx } from "cva";
import { type Config, usePopperTooltip } from "react-popper-tooltip";
import type { FC, PropsWithChildren, ReactNode } from "react";

export const tooltipRecipe = cva({
  base: "kn-tooltip",
  variants: {
    theme: {
      light: "kn-tooltip-light",
      dark: "kn-tooltip-dark",
    },
  },
  defaultVariants: {
    theme: "light",
  },
});

type TooltipRecipeVariants = VariantProps<typeof tooltipRecipe>;

export type TooltipProps = PropsWithChildren<
  {
    arrowClassName?: string;
    content: ReactNode;
    contentClassName?: string;
    className?: string;
    placement?: Config["placement"];
    trigger?: Config["trigger"];
  } & TooltipRecipeVariants
>;

const Tooltip: FC<TooltipProps> = ({
  arrowClassName,
  children,
  className,
  contentClassName,
  content,
  placement,
  trigger,
  theme = "light",
}) => {
  const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } =
    usePopperTooltip({
      placement,
      trigger,
    });

  return (
    <>
      <div ref={setTriggerRef} className={className}>
        {children}
      </div>
      {visible
        ? createPortal(
            <div
              ref={setTooltipRef}
              {...getTooltipProps({
                className: cx("tooltip-container", tooltipRecipe({ theme }), contentClassName),
              })}
            >
              <div
                {...getArrowProps({
                  className: cx("kn-tooltip-arrow", arrowClassName),
                })}
              />
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

Tooltip.displayName = "Tooltip";

export default Tooltip;
